import { useUser } from '../../../Context/useUser';
import { useParams } from 'react-router';
import { useVisitingUser } from '../../../Hooks/useVisitingUser';

import styles from './TestsPage.module.css';
import Line from '../../../Components/Charts/Line/Line';
import { useEffect, useMemo, useState } from 'react';
import API from '../../../Utils/api';
import Table from '../../../Components/Table/Table';
import InfoPopover from '../../../Components/InfoPopover/InfoPopover';
import ChartHtmlLegend, {
    useChartHtmlLegend,
    useViewportWidth
} from '../../../Components/Charts/HtmlLegend/ChartHtmlLegend';
import { toast } from 'react-toastify';

const TEST_TYPE_LABELS = {
    'Type:25': '25M-400M',
    'Type:30': 'T30 & 50M',
    'Type:50': '50M - 100M - 200M - 400M'
};

// Glossary term behind each chart label (the MSS label says "Maximum", the glossary "Maximal").
const LEGEND_TERMS = {
    'Critical Swim Speed (CSS)': 'Critical Swim Speed',
    'Maximum Swim Speed (MSS)': 'Maximal Swim Speed'
};

export default function AllTestsPage () {
    const columns = [
        {
            Header: 'Date',
            accessor: 'testDate',
            Cell: ({ row }) => {
                const date = new Date(row.original?.testDate);
                return (
                    <div>
                        {date.toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                        })}
                    </div>
                );
            }
        },
        {
            id: 'test',
            Header: () => <div className={styles.center}>Test</div>,
            accessor: 'type',
            Cell: ({ row }) => <div className={styles.center}>{TEST_TYPE_LABELS[row.original?.type] || 'N/A'}</div>
        },
        {
            id: 'pool',
            Header: () => <div className={styles.center}>Pool</div>,
            accessor: 'pool',
            Cell: ({ row }) => (
                <div className={styles.center}>{row.original.poolSize ? row.original.poolSize + 'm' : 'N/A'}</div>
            )
        },
        {
            id: 'css',
            Header: () => <div className={styles.center}>
                    Critical Swim Speed (CSS)
                    <InfoPopover term='Critical Swim Speed' />
                </div>,
            accessor: 'css',
            Cell: ({ row }) => <div className={styles.center}>{row.original.css?.toFixed(2)}</div>
        },
        {
            id: 'mss',
            Header: () => <div className={styles.center}>
                    Maximum Swim Speed (MSS)
                    <InfoPopover term='Maximal Swim Speed' />
                </div>,
            accessor: 'mss',
            Cell: ({ row }) => <div className={styles.center}>{row.original.mss?.toFixed(2)}</div>
        }
    ];

    const { user } = useUser();
    const width = useViewportWidth();
    const legend = useChartHtmlLegend();

    const { athleteId } = useParams();

    const visitedUser = useVisitingUser(athleteId);

    const currentUser = athleteId ? visitedUser : user;

    const [allTests, setAllTests] = useState([]);
    const [testsLoading, setTestsLoading] = useState(true);
    const [selectedRows, setSelectedRows] = useState([]);

    const handleRowClick = row => {
        setSelectedRows(prev => {
            const alreadySelected = prev.some(r => r._id === row._id);
            if (alreadySelected) {
                return prev.filter(r => r._id !== row._id);
            } else {
                return [...prev, row];
            }
        });
    };

    const chartData = useMemo(() => {
        const data = selectedRows.length > 0 ? selectedRows : allTests;
        // Sort by testDate ascending
        return [...data].sort((a, b) => new Date(a.testDate) - new Date(b.testDate));
    }, [selectedRows, allTests]);
    
    const fetchAndSetData = async currentUser => {
        if (!currentUser) return;

        setTestsLoading(true);
        try {
            const { data } = await API.getAllTestsForUser(currentUser._id);
            if (data) {
                setAllTests(data);
            }
        } catch (error) {
            toast.error(`Failed to fetch tests - please contact support`, {
                position: 'bottom-center'
            });
        } finally {
            setTestsLoading(false);
        }
    };

    const labelsMeters = useMemo(
        () =>
            chartData.map(record => {
                const date = new Date(record.testDate);
                // Format: dd/mm/yyyy hh:mm (no seconds)
                return date.toLocaleString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                });
            }),
        [chartData]
    );

    const datasetsMeters = useMemo(
        () => [
            {
                label: 'Critical Swim Speed (CSS)',
                data: chartData.map(record => record.css),
                borderColor: 'rgb(54, 162, 235)',
                backgroundColor: 'rgba(54, 162, 235, 0.5)',
                tension: 0.3
            },
            {
                label: 'Maximum Swim Speed (MSS)',
                data: chartData.map(record => record.mss),
                borderColor: 'rgb(255, 99, 132)',
                backgroundColor: 'rgba(255, 99, 132, 0.5)',
                tension: 0.3
            }
        ],
        [chartData]
    );

    const chartOptions = useMemo(
        () => ({
            plugins: {
                legend: {
                    // Replaced by the accessible HTML legend (ChartHtmlLegend), same labels.
                    display: false,
                    labels: {
                        generateLabels: chart =>
                            chart.data.datasets.map((dataset, i) => ({
                                text: dataset.label,
                                strokeStyle: dataset.borderColor,
                                fillStyle: dataset.borderColor,
                                hidden: !chart.isDatasetVisible(i),
                                datasetIndex: i
                            }))
                    }
                }
            }
        }),
        []
    );

    const legendItems = useMemo(
        () => legend.items.map(item => ({ ...item, term: LEGEND_TERMS[item.text] })),
        [legend.items]
    );

    useEffect(() => {
        fetchAndSetData(currentUser);
    }, [currentUser]);

    useEffect(() => {
        setSelectedRows(allTests);
    }, [allTests]);
    return (
        <div className={styles.testPage} data-tests-page>
            <div className={styles.allTestsWorkspace} data-tests-workspace>
                <section className={styles.panel} aria-labelledby='all-history-title' data-tests-history>
                    <div className={styles.panelHead}>
                        <div className={styles.panelHeadText}>
                            <h2 id='all-history-title' className={styles.panelTitle}>
                                Test history
                            </h2>
                            <p className={styles.panelSub}>
                                Select rows to choose which tests appear on the chart. With no rows selected, the
                                chart shows every test.
                            </p>
                        </div>
                        {!testsLoading && allTests.length > 0 && (
                            <span className={styles.selectionCount} data-selection-count>
                                {selectedRows.length > 0
                                    ? `${selectedRows.length} of ${allTests.length} tests on the chart`
                                    : `All ${allTests.length} tests on the chart`}
                            </span>
                        )}
                    </div>
                    <div className={styles.panelFlush}>
                        <Table
                            onRowClick={handleRowClick}
                            columns={columns}
                            data={allTests}
                            isLoading={testsLoading}
                            rowsPerPage={10}
                            updatePageSize={false}
                            pageSkipping={false}
                            selected={selectedRows}
                            showSelectionIndicator={true}
                        />
                    </div>
                </section>
                <section className={styles.panel} aria-labelledby='all-chart-title'>
                    <div className={styles.panelHead}>
                        <div className={styles.panelHeadText}>
                            <h2 id='all-chart-title' className={styles.panelTitle}>
                                CSS & MSS over time
                            </h2>
                            <p className={styles.panelSub}>
                                Velocity in m/sec. Use the legend to show or hide a series.
                            </p>
                        </div>
                    </div>
                    <div className={styles.chartStack}>
                        <ChartHtmlLegend
                            items={legendItems}
                            onToggle={legend.toggle}
                            fontSize={width < 768 ? 10 : 12}
                        />
                        <div className={styles.chartPlot}>
                            <Line
                                title={'CSS & MSS over time'}
                                titleY={'Velocity(m/sec)'}
                                titleX={'Date (dd/mm/yyyy hh:mm:ss)'}
                                datasets={datasetsMeters}
                                labels={labelsMeters}
                                xAxisType='category'
                                options={chartOptions}
                                plugins={legend.plugins}
                            />
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}
